import { datadogRum } from '@datadog/browser-rum'
import { invariant } from '@seen/core/invariant'
import type { Transaction } from '@tiptap/pm/state'
import { ReplaceAroundStep, ReplaceStep } from '@tiptap/pm/transform'
import type { Editor } from '@tiptap/react'
import { isMachineContentSync } from '#src/components/rich-text-editor/machineContentSync.ts'
import {
  isUndoRedoOperation,
  isYjsOrigin,
} from '#src/components/rich-text-editor/transactionOrigin.ts'

// Observation-only instrumentation for the visit-note caret report: while a
// clinician types, the caret sometimes lands somewhere they did not put it.
// Several mechanisms have been proposed and none has survived, so this module
// takes no position on any of them. It records measurements — positions,
// sizes, step shapes, origins, timings — and identifiers that join those
// measurements to the rest of the record: `noteId` (the Composition id),
// `mountId` (random per editor mount, telling a remount from a continuing
// mount and two tabs from one), and `contentVersionId` (the Medplum
// `meta.versionId` of the content this mount loaded, which joins the event
// stream to the resource history). Which of those measurements add up to a
// caret jump is a question for the investigation that reads them, across
// sessions and alongside the Medplum log, not for the tab that emits them.
//
// Nothing here is a verdict. The two numbers that decide whether a transaction
// is emitted at all are volume triggers, and both deliberately over-emit: a
// transaction is recorded when the caret landed anywhere other than where the
// batch's own position mapping carries it, or when a single step covered at
// least half the document. Everything the trigger looked at is on the event,
// so a reader can apply a tighter rule later; the reverse is not possible.
//
// Every attribute is a count, an offset, a boolean, a duration or a flag value.
// Note text, block text and anything else derived from clinical content is
// deliberately absent: these events cross into a telemetry sink.

export type TransactionSource =
  | 'machineSync'
  | 'undoRedo'
  | 'yjsRemote'
  | 'local'

export type ReconcileOutcome =
  | 'blocked:notReady'
  | 'blocked:notSynchronized'
  | 'blocked:resourceNotLoaded'
  | 'blocked:pendingResourceWrite'
  | 'blocked:othersPresent'
  | 'blocked:committedAccept'
  | 'latched:noDrift'
  | 'latched:wrote'
  | 'skipped:othersPresent'
  | 'skipped:localEdit'

// Most-specific-first: a machine content sync is also carried over Yjs and
// would otherwise read as a remote peer edit, and an undo of a machine sync
// carries both markers too. Any transaction in the batch carrying a marker
// classifies the batch. This reads markers the transaction already carries; it
// infers nothing.
export function classifyTransactionSource(
  transactions: readonly Transaction[]
): TransactionSource {
  if (transactions.some(isMachineContentSync)) {
    return 'machineSync'
  }
  if (transactions.some(isUndoRedoOperation)) {
    return 'undoRedo'
  }
  if (transactions.some(isYjsOrigin)) {
    return 'yjsRemote'
  }
  return 'local'
}

export type KeyClass =
  | 'printable'
  | 'editing'
  | 'navigation'
  | 'shortcut'
  | 'modifier'
  | 'composition'
  | 'other'

// The shape of the last key pressed, never the key. A caret move "right after
// typing" means something different after a printable character than after an
// arrow key or Cmd+Z, and only the class is needed to tell them apart; the
// character itself would be note content.
export function classifyKey(
  event: Pick<
    KeyboardEvent,
    'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'isComposing'
  >
): KeyClass {
  if (event.isComposing || event.key === 'Process' || event.key === 'Dead') {
    return 'composition'
  }
  if (event.metaKey || event.ctrlKey) {
    return 'shortcut'
  }
  switch (event.key) {
    case 'Shift':
    case 'Control':
    case 'Alt':
    case 'Meta':
    case 'CapsLock':
      return 'modifier'
    case 'Backspace':
    case 'Delete':
    case 'Enter':
      return 'editing'
    case 'ArrowLeft':
    case 'ArrowRight':
    case 'ArrowUp':
    case 'ArrowDown':
    case 'Home':
    case 'End':
    case 'PageUp':
    case 'PageDown':
    case 'Tab':
      return 'navigation'
  }
  // Alt alone composes accented characters on macOS, which still arrive as a
  // single printable key.
  if (event.key.length === 1) {
    return 'printable'
  }
  return 'other'
}

// A tiptap `transaction` event carries the dispatched transaction plus every
// transaction a plugin appended to it, and reports editor state as it stands
// AFTER all of them have been applied. Mapping a position through the root
// alone and then comparing it against that state manufactures drift wherever a
// plugin appended a step, and conceals real drift wherever an appended step
// happens to cancel it out. Every position measured here is carried through
// the whole batch, in the order it was applied.
export function mapThroughBatch(
  transactions: readonly Transaction[],
  position: number
): number {
  return transactions.reduce(
    (mapped, tr) => tr.mapping.map(mapped, 1),
    position
  )
}

export interface CaretDrift {
  caretBefore: number
  // Where the caret lands if it is carried through this batch's changes.
  // Identical to caretBefore when the batch changed no content.
  caretExpected: number
  // Signed actual minus expected: positive means the caret ended up further
  // into the document than the edit accounts for.
  caretDrift: number
}

// Null when the pre-transaction caret is unknown, which is the case for the
// first transaction after mount: with nothing to map, no drift is measurable.
// Bias 1 resolves a position sitting exactly at an insertion point forward,
// which is where the caret goes when the clinician types at it.
//
// Across a step that replaces the whole document this number is not wrong,
// but it means something different: the mapping sends every pre-image
// position to the tail of the replacement, so a selection y-prosemirror
// restored to its original place reads as large drift and a selection that
// collapsed to the tail reads as none. `stepCoverage` on the same event is
// what lets a reader tell those two regimes apart.
export function measureCaretDrift(
  transactions: readonly Transaction[],
  caretBefore: number | null,
  caretAfter: number
): CaretDrift | null {
  if (caretBefore === null) {
    return null
  }
  const caretExpected = mapThroughBatch(transactions, caretBefore)
  return { caretBefore, caretExpected, caretDrift: caretAfter - caretExpected }
}

export interface BatchContentMetrics {
  docEndBefore: number
  docEndAfter: number
  blocksBefore: number
  blocksAfter: number
  charsBefore: number
  charsAfter: number
  charsDelta: number
}

// Measured across the whole batch: the document before the root transaction
// against the document after the last appended one. Taking both ends off the
// root alone would report the intermediate state a plugin then edited again.
// `docEndBefore` is the document `caretBefore` was an offset into, which a
// reader needs to place that offset at all.
export function batchContentMetrics(
  transactions: readonly Transaction[]
): BatchContentMetrics {
  const first = transactions[0]
  const last = transactions[transactions.length - 1]
  invariant(first && last, 'batchContentMetrics needs at least one transaction')
  const charsBefore = first.before.textContent.length
  const charsAfter = last.doc.textContent.length
  return {
    docEndBefore: first.before.content.size,
    docEndAfter: last.doc.content.size,
    blocksBefore: first.before.childCount,
    blocksAfter: last.doc.childCount,
    charsBefore,
    charsAfter,
    charsDelta: charsAfter - charsBefore,
  }
}

export type StepKind = 'replace' | 'replaceAround'

export interface LargestStep {
  // Null when no step in the batch replaced content (a selection-only
  // transaction, or one made of mark, attr or structural steps).
  stepKind: StepKind | null
  stepFrom: number | null
  stepTo: number | null
  // Size of the document as it stood before this step, against which
  // `stepFrom`/`stepTo` are offsets. Per step, not per batch: an appended
  // step sees the document the root already changed.
  stepSizeBefore: number | null
  // `(stepTo - stepFrom) / stepSizeBefore`, so 1 is a step spanning the whole
  // document and a typed character is a few thousandths.
  stepCoverage: number | null
  // For a ReplaceAroundStep, the size of the range it carried through
  // untouched. A step that spans the document but keeps a gap almost as large
  // changed only the nodes around the content — a heading toggle or a list
  // wrap in a one-block note produces exactly that. Null for a ReplaceStep.
  gapSize: number | null
}

// The single content step with the widest coverage across the batch, reported
// with its raw shape. y-prosemirror's sync plugin rebuilds the whole fragment
// and dispatches `tr.replace(0, doc.content.size, ...)` for EVERY remote
// update, however small (`_typeChanged`, y-prosemirror 1.3.7), so coverage of
// 1 with `source: 'yjsRemote'` is the routine shape of a remote edit, not a
// finding — and a second client in the room produces one per keystroke.
export function largestStep(transactions: readonly Transaction[]): LargestStep {
  let largest: LargestStep = {
    stepKind: null,
    stepFrom: null,
    stepTo: null,
    stepSizeBefore: null,
    stepCoverage: null,
    gapSize: null,
  }
  for (const tr of transactions) {
    for (const [index, step] of tr.steps.entries()) {
      if (!(step instanceof ReplaceStep || step instanceof ReplaceAroundStep)) {
        continue
      }
      const sizeBefore = (tr.docs[index] ?? tr.before).content.size
      const coverage = sizeBefore === 0 ? 0 : (step.to - step.from) / sizeBefore
      if (largest.stepCoverage !== null && coverage <= largest.stepCoverage) {
        continue
      }
      largest = {
        stepKind:
          step instanceof ReplaceAroundStep ? 'replaceAround' : 'replace',
        stepFrom: step.from,
        stepTo: step.to,
        stepSizeBefore: sizeBefore,
        stepCoverage: coverage,
        gapSize:
          step instanceof ReplaceAroundStep ? step.gapTo - step.gapFrom : null,
      }
    }
  }
  return largest
}

export type TransactionTrigger = 'drift' | 'replace' | 'both'

// A step covering at least this fraction of the document is recorded whether
// or not the caret moved. Half is a volume guard, not a claim about what
// matters: it keeps typing and ordinary edits out while admitting every shape
// that has ever been proposed, and the coverage itself is on the event.
export const REPLACE_TRIGGER_COVERAGE = 0.5

// Why a transaction was recorded, or null when it was not. Both conditions are
// measurements the event carries verbatim: `drift` is any caret landing
// off the mapped position, including a plain arrow key, and `replace` is
// coverage at or past the threshold above. A reader wanting a stricter rule
// applies it to the fields; the emitter does not pre-empt them.
export function transactionTrigger({
  caretDrift,
  stepCoverage,
}: {
  caretDrift: number | null
  stepCoverage: number | null
}): TransactionTrigger | null {
  const drift = caretDrift !== null && caretDrift !== 0
  const replace =
    stepCoverage !== null && stepCoverage >= REPLACE_TRIGGER_COVERAGE
  if (drift && replace) {
    return 'both'
  }
  if (drift) {
    return 'drift'
  }
  if (replace) {
    return 'replace'
  }
  return null
}

export interface EmissionBudget {
  // True when this emission fits the budget; false when it is dropped. Dropped
  // emissions are counted so the coverage event can say how many.
  tryConsume: (now: number) => boolean
  readonly dropped: number
}

// The Datadog browser SDK drops custom actions past 3000 per minute
// (`EVENT_RATE_LIMIT`, @datadog/browser-core 6.25), silently from this code's
// point of view. This budget sits well under that so the stream degrades
// predictably and reports its own losses. A rolling window rather than a
// per-mount total: a note left open all afternoon must not go permanently
// blind after one burst of navigation.
export const TRANSACTION_EVENTS_PER_WINDOW = 120
export const TRANSACTION_EVENT_WINDOW_MS = 60 * 1000

export function createEmissionBudget(
  limit = TRANSACTION_EVENTS_PER_WINDOW,
  windowMs = TRANSACTION_EVENT_WINDOW_MS
): EmissionBudget {
  let windowStart = 0
  let used = 0
  let dropped = 0
  return {
    tryConsume(now) {
      if (now - windowStart >= windowMs) {
        windowStart = now
        used = 0
      }
      if (used >= limit) {
        dropped += 1
        return false
      }
      used += 1
      return true
    },
    get dropped() {
      return dropped
    },
  }
}

export interface ReconcileDecisionThrottle {
  shouldEmit: (outcome: ReconcileOutcome) => boolean
}

// The reconcile effect re-runs on every autosave, so an unthrottled reporter
// would bury the interesting decisions under thousands of identical blocked
// ones. Only a CHANGE of outcome is worth a data point — except the write,
// which is the one outcome that changes the document and therefore always
// emits. The coverage event carries the evaluation count so the suppression
// is visible.
export function createReconcileDecisionThrottle(): ReconcileDecisionThrottle {
  let lastOutcome: ReconcileOutcome | null = null
  return {
    shouldEmit(outcome) {
      if (outcome === 'latched:wrote') {
        lastOutcome = outcome
        return true
      }
      if (outcome === lastOutcome) {
        return false
      }
      lastOutcome = outcome
      return true
    },
  }
}

function normalizeText(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

export interface ReconcileContentStats {
  initialContentChars: number
  initialContentBlocks: number
  editorChars: number | null
  editorBlocks: number | null
}

// Sizes of both sides of the reconcile compare, in the same units on each
// side: whitespace-normalized text length, and top-level node count — the
// incoming HTML's direct children of <body>, the live document's direct
// children of the doc node. A list is one block on both sides, however many
// items it holds; the one systematic difference is the empty trailing
// paragraph the editor schema appends (TrailingNode), which the live side
// carries and stored HTML does not. The incoming side is the Medplum version named by
// `contentVersionId`, so its exact content is on the resource history; the
// live side exists only in the room, and its size is all this can carry.
// Whether the two differ, and in which direction, is for the reader with both
// in hand. Called only once a decision has cleared the throttle, since both
// sides cost a document walk.
export function reconcileContentStats(
  editor: Editor | null,
  initialContent: string
): ReconcileContentStats {
  const incomingDoc = new DOMParser().parseFromString(
    initialContent,
    'text/html'
  )
  const incoming = {
    initialContentChars: normalizeText(incomingDoc.body.textContent ?? '')
      .length,
    initialContentBlocks: incomingDoc.body.childElementCount,
  }
  if (!editor) {
    return { ...incoming, editorChars: null, editorBlocks: null }
  }
  const doc = editor.state.doc
  return {
    ...incoming,
    editorChars: normalizeText(doc.textContent).length,
    editorBlocks: doc.childCount,
  }
}

// Callers must wrap the WHOLE observation, not just the emission. Building
// these events walks the document, maps positions through a transaction and
// parses stored HTML, and every one of those runs either inside a ProseMirror
// dispatch or — in the reconcile case — on the line before the real
// setContent. A guard around the sink alone leaves all of that unprotected, so
// an observer bug would still cost the clinician the note it exists to watch.
export function observeSafely(observe: () => void): void {
  try {
    observe()
  } catch {
    // Intentionally silent — see above.
  }
}

export interface MountIdentity {
  noteId: string | null
  mountId: string
  // Medplum `meta.versionId` of the content this mount loaded, or null where
  // the host has none to give. The join key from this stream to the resource
  // history: which saves preceded a mount, and which version a reconcile wrote.
  contentVersionId: string | null
  msSinceMount: number
}

export interface TransactionObservedEvent
  extends MountIdentity, BatchContentMetrics, LargestStep {
  trigger: TransactionTrigger
  source: TransactionSource
  docChanged: boolean
  selectionSet: boolean
  // How many transactions a plugin appended to the one that was dispatched.
  appendedCount: number
  caretBefore: number | null
  caretExpected: number | null
  caretDrift: number | null
  caretAfter: number
  // Relative to this event's own timestamp, so each is an absolute time once
  // the event's time is known. Null when the thing never happened on this
  // mount. `msSinceLastLocalEdit` against `msSinceLastSave` is how a reader
  // tells whether the room held changes the server had not yet been sent.
  msSinceKeyDown: number | null
  // Class of the key behind `msSinceKeyDown`; null until a key is pressed.
  lastKeyClass: KeyClass | null
  // Pointer events are observed on the document, not the editor, so a click
  // on a toolbar button, a mention popup or a dialog counts as a click. This
  // says whether the last one landed inside the editor's own DOM.
  msSincePointerDown: number | null
  lastPointerDownInsideEditor: boolean | null
  msSinceLastLocalEdit: number | null
  msSinceLastSave: number | null
  // Since the last transaction on this mount whose largest step met the
  // replace trigger, recorded or dropped.
  msSinceLastReplace: number | null
  editorFocused: boolean
  othersCount: number
  isDocSynchronized: boolean
  saveOnAnyLocalEdit: boolean
}

// One transaction batch, recorded because a volume trigger admitted it. Every
// field is a measurement or a marker; `trigger` says which condition let it
// through so a reader can separate the two populations.
//
// Datadog query: `@action.name:"note.transaction.observed"`, grouped by
// `@context.trigger`, `@context.source` and `@context.stepCoverage`.
export function reportTransactionObserved(
  event: TransactionObservedEvent
): void {
  datadogRum.addAction('note.transaction.observed', { ...event })
}

export interface ReconcileDecisionEvent
  extends MountIdentity, ReconcileContentStats {
  outcome: ReconcileOutcome
  othersCount: number
  isDocSynchronized: boolean
  isResourceLoaded: boolean
  hasPendingResourceWrite: boolean
}

// Fired from the mount-time Medplum -> Liveblocks reconcile, reporting which
// gate stopped it or that it wrote. `othersCount` is the load-bearing one:
// BigQuery heartbeats can only approximate room presence (an idle tab emits
// nothing, and a connection-loss clearOthers() is invisible to them), so the
// value the hook actually saw settles in one occurrence what the heartbeats
// cannot. Throttled by createReconcileDecisionThrottle at the call site.
//
// Datadog query: `@action.name:"note.reconcile.decided"`
export function reportReconcileDecision(event: ReconcileDecisionEvent): void {
  datadogRum.addAction('note.reconcile.decided', { ...event })
}

// Long enough that a normal note visit reports at enable and at unmount, and
// only a mount left open for a working session reports on the timer.
export const DIAGNOSTICS_COVERAGE_INTERVAL_MS = 15 * 60 * 1000

export interface DiagnosticsCounts {
  transactions: number
  docChangedTransactions: number
  selectionSetTransactions: number
  keyDowns: number
  pointerDowns: number
  driftTriggers: number
  replaceTriggers: number
  transactionsEmitted: number
  // Admitted by a trigger but dropped by the emission budget. Non-zero means
  // the transaction stream for this mount has gaps.
  transactionsDropped: number
  reconcileEvaluations: number
  reconcileDecisionsEmitted: number
}

export interface DiagnosticsCoverageEvent
  extends MountIdentity, DiagnosticsCounts {
  // 'enabled' arrives once, when the flag evaluated on for this mount — the
  // one emission a tab closed mid-session still leaves behind, since a close
  // runs no unmount. 'interval' keeps a long sitting mount from reporting
  // nothing until it does unmount.
  reason: 'enabled' | 'interval' | 'unmount'
}

// What this instrument actually saw, whether or not it recorded anything.
//
// The transaction event only fires when a trigger admits it, which makes its
// silence unreadable on its own: no events can mean the editor behaved, or
// that the flag never evaluated on, or that the clinician never typed, or that
// the budget dropped everything, or that a bug in the observer swallowed it.
// This is the denominator, with the drop count that says whether the recorded
// stream is complete.
//
// Datadog query: `@action.name:"note.diagnostics.sampled"`
export function reportDiagnosticsCoverage(
  event: DiagnosticsCoverageEvent
): void {
  datadogRum.addAction('note.diagnostics.sampled', { ...event })
}
